import React, { PropsWithChildren, useEffect, useState } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';

import './RteComponent.css';
import { useWidthContext } from '../../providers/Width';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import {
    getMarginFromProps,
    getPaddingFromProps,
} from '../../utils/cob-utils';
import { RefreshEvent } from '../../types/shared/RefreshEvent';
import {
    IMarginControl,
    IPaddingControl,
} from '../../types/shared/SpacingControl';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { Row, Col, Container } from '../../importCL';
interface IRteComponentProps
    extends MappedComponentProperties,
    IPaddingControl,
    IMarginControl {
    leftJustify?: boolean;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    text?: string;
    mobileText?: string;
    dialogValues?: boolean;
    cqType?: string;
}

export const RteComponentEditConfig = {
    emptyLabel: 'RTE Component',
    isEmpty: (props: PropsWithChildren<IRteComponentProps>) => {
        return !props || !props.text;
    },
};

type TDynamicTermsData = {
    CalculatedPurchaseRate: number,
    CalculatedCashRate: number,
    CalculatedTransferRate: number,
    CalculatedIntroTransferRate: number,
    ProgramCode: string,
} | null;

type TDynamicUnifiedTerms = {
    lowApr: number,
    highApr: number,
    size: number,
} | null;

const RteComponent = (
    props: PropsWithChildren<IRteComponentProps>,
): JSX.Element => {
    const { width } = useWidthContext();
    const padding = getPaddingFromProps(props, width, true);
    const margin = getMarginFromProps(props, width, true);
    const termsPage = props.cqPath.includes('/terms');
    const disclosuresPage = props.cqPath.includes('/disclosures');
    const description = width > 768 ? props.text : props.mobileText;
    const [rewriteText, setRewriteText] = useState(description);
    const [dynamicTermsData] = useState<TDynamicTermsData>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicTermsData') ?? '{}'));
    const [dynamicUnifiedPurchaseTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedPurchaseTerms') ?? '{}'));
    const [dynamicUnifiedMlaPurchaseTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedMlaPurchaseTerms') ?? '{}'));

    const [dynamicUnifiedCashTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedCashTerms') ?? '{}'));
    const [dynamicUnifiedMlaCashTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedMlaCashTerms') ?? '{}'));

    const [dynamicUnifiedTransferTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedTransferTerms') ?? '{}'));
    const [dynamicUnifiedMlaTransferTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedMlaTransferTerms') ?? '{}'));

    const [dynamicUnifiedIntroTransferTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedIntroTransferTerms') ?? '{}'));
    const [dynamicUnifiedMlaIntroTransferTerms] = useState<TDynamicUnifiedTerms>(JSON.parse(PathUtils.getMetaPropertyValue('cq:dynamicUnifiedMlaIntroTransferTerms') ?? '{}'));

    const today = new Date();
    const currentYear = today.getFullYear();

    const createUnifiedTermString = (lowApr: number, highApr: number, size: number) => {
        if (size === 1) {
            return `${lowApr}%`;
        } else if (size === 2) {
            return `${lowApr}% or ${highApr}%`;
        } else if (size > 2) {
            return `${lowApr}% to ${highApr}%`;
        }
    };

    const termsMap: readonly {
        key: string,
        regex: RegExp,
        limit?: () => boolean,
        value: string | undefined | null
    }[] = [
        {
            key: 'CalculatedPurchaseRate',
            regex: /\{CalculatedPurchaseRate\}/g,
            value: (dynamicTermsData && typeof dynamicTermsData.CalculatedPurchaseRate === 'number') ? `${dynamicTermsData?.CalculatedPurchaseRate}` : null,
        },
        {
            key: 'MonthlyPeriodicRate',
            regex: /\{MonthlyPeriodicRate\}/g,
            value: (dynamicTermsData && typeof dynamicTermsData.CalculatedPurchaseRate === 'number') ? `${(dynamicTermsData?.CalculatedPurchaseRate / 12).toPrecision(5)}%` : null,
        },
        {
            key: 'CalculatedCashRate',
            regex: /\{CalculatedCashRate\}/g,
            value: (dynamicTermsData && typeof dynamicTermsData.CalculatedCashRate === 'number') ? `${dynamicTermsData?.CalculatedCashRate}` : null,
        },
        {
            key: 'CalculatedTransferRate',
            regex: /\{CalculatedTransferRate\}/g,
            value: (dynamicTermsData && typeof dynamicTermsData?.CalculatedTransferRate === 'number') ? `${dynamicTermsData.CalculatedTransferRate}` : null,
        },
        {
            key: 'CalculatedIntroTransferRate',
            regex: /\{CalculatedIntroTransferRate\}/g,
            value: (dynamicTermsData && typeof dynamicTermsData?.CalculatedIntroTransferRate === 'number') ? `${dynamicTermsData.CalculatedIntroTransferRate}` : null,
        },
        {
            key: 'ProgramCode',
            regex: /\{ProgramCode\}/g,
            value: (dynamicTermsData && dynamicTermsData.ProgramCode !== '') ? dynamicTermsData.ProgramCode : null,
        },
        {
            key: 'PrimeDate',
            regex: /<span class="prime-rate-date">.*?<\/span>/g,
            limit: () => (termsPage || disclosuresPage),
            value: `<span class="prime-rate-date">${new Date(today.getFullYear(), today.getMonth(), 0).toLocaleDateString()}</span>`,
        },
        {
            key: 'Year',
            regex: /\{\{year\}\}/g,
            value: currentYear.toString(),
        },
        {
            key: 'UnifiedCalculatedPurchaseRate',
            regex: /\{UnifiedCalculatedPurchaseRate\}/g,
            value: (dynamicUnifiedPurchaseTerms && dynamicUnifiedPurchaseTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedPurchaseTerms.lowApr, dynamicUnifiedPurchaseTerms.highApr, dynamicUnifiedPurchaseTerms.size) : null,
        },
        {
            key: 'UnifiedCalculatedCashRate',
            regex: /\{UnifiedCalculatedCashRate\}/g,
            value: (dynamicUnifiedCashTerms && dynamicUnifiedCashTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedCashTerms.lowApr, dynamicUnifiedCashTerms.highApr, dynamicUnifiedCashTerms.size) : null,
        },
        {
            key: 'UnifiedCalculatedTransferRate',
            regex: /\{UnifiedCalculatedTransferRate\}/g,
            value: (dynamicUnifiedTransferTerms && dynamicUnifiedTransferTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedTransferTerms.lowApr, dynamicUnifiedTransferTerms.highApr, dynamicUnifiedTransferTerms.size) : null,
        },
        {
            key: 'UnifiedCalculatedIntroTransferRate',
            regex: /\{UnifiedCalculatedIntroTransferRate\}/g,
            value: (dynamicUnifiedIntroTransferTerms && dynamicUnifiedIntroTransferTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedIntroTransferTerms.lowApr, dynamicUnifiedIntroTransferTerms.highApr, dynamicUnifiedIntroTransferTerms.size) : null,
        },
        {
            key: 'UnifiedMLACalculatedPurchaseRate',
            regex: /\{UnifiedMLACalculatedPurchaseRate\}/g,
            value: (dynamicUnifiedMlaPurchaseTerms && dynamicUnifiedMlaPurchaseTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedMlaPurchaseTerms.lowApr, dynamicUnifiedMlaPurchaseTerms.highApr, dynamicUnifiedMlaPurchaseTerms.size) : null,
        },
        {
            key: 'UnifiedMLACalculatedCashRate',
            regex: /\{UnifiedMLACalculatedCashRate\}/g,
            value: (dynamicUnifiedMlaCashTerms && dynamicUnifiedMlaCashTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedMlaCashTerms.lowApr, dynamicUnifiedMlaCashTerms.highApr, dynamicUnifiedMlaCashTerms.size) : null,
        },
        {
            key: 'UnifiedMLACalculatedTransferRate',
            regex: /\{UnifiedMLACalculatedTransferRate\}/g,
            value: (dynamicUnifiedMlaTransferTerms && dynamicUnifiedMlaTransferTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedMlaTransferTerms.lowApr, dynamicUnifiedMlaTransferTerms.highApr, dynamicUnifiedMlaTransferTerms.size) : null,
        },
        {
            key: 'UnifiedMLACalculatedIntroTransferRate',
            regex: /\{UnifiedMLACalculatedIntroTransferRate\}/g,
            value: (dynamicUnifiedMlaIntroTransferTerms && dynamicUnifiedMlaIntroTransferTerms.size !== 0) ? createUnifiedTermString(dynamicUnifiedMlaIntroTransferTerms.lowApr, dynamicUnifiedMlaIntroTransferTerms.highApr, dynamicUnifiedMlaIntroTransferTerms.size) : null,
        },
    ] as const;

    const invalidateTerms = async () => {
        if (window.frameElement) {
            window.parent.postMessage('terms_invalid', '*');
        }
    };

    const updateText = () => {
        if (description) {
            let text = description;
            termsMap.forEach(({ regex, value, limit }) => {
                if (
                    regex.test(text!) &&
                    ((!limit || limit()))
                ) {
                    if (value) {
                        text = text!.replace(regex, value);
                    } else {
                        // Invalid Value, disable submission
                        invalidateTerms();
                    }
                }
            });
            setRewriteText(text);
        }
    };

    const refreshComponent = (event: Event) => {
        const ev: RefreshEvent = event as RefreshEvent;
        if (
            !ev.detail ||
            ev.detail.type !== props.cqType ||
            ev.detail.path !== props.cqPath
        ) {
            return;
        }
        const newProps = Object.assign({}, props);
        Object.assign(newProps, ev.detail.data);
        props = newProps;
        updateText();
    };

    useEffect(() => {
        updateText();
        if (props.isInEditor) {
            window.parent.addEventListener(
                'internal-component-refresh-event',
                refreshComponent,
            );

            return () => {
                window.parent.removeEventListener(
                    'internal-component-refresh-event',
                    refreshComponent,
                );
            };
        }
    }, []);

    return (
        <div
            id='rte-component'
            className={`RteComponent 
            ${termsPage ? 'RteComponent--termsPage' : ''} 
            ${!props.isInEditor && props.hideOnDesktop ? 'RteComponent--hideOnDesktop' : ''}
            ${!props.isInEditor && props.hideOnMobile ? 'RteComponent--hideOnMobile' : ''}`}
            style={{
                ...padding,
                ...margin,
            }}
        >
            <Container
                className={`${props.leftJustify ? 'RteComponent__container--leftJustify' : ''
                }`}
            >
                <Row>
                    {rewriteText && (
                        <Col className='RteComponent__col'>
                            {transformHTMLParserLinks(rewriteText)}
                        </Col>
                    )}
                </Row>
            </Container>
        </div>
    );
};

export default RteComponent;
